import React, { useEffect, useId, useMemo, useRef, useState } from 'react';

const normalizeText = text =>
  (text || '')
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

const defaultFilterOptions = (options, searchTerm) => {
  if (!options) return [];
  const term = (searchTerm || '').trim();
  const normalizedTerm = normalizeText(term);
  if (!normalizedTerm) return options.filter(option => !option.divider);

  return options
    .filter(option => !option.divider)
    .map(option => {
      const label = option.label || '';
      const normalizedLabel = normalizeText(label);
      if (normalizedLabel.startsWith(normalizedTerm)) return { option, score: 0 };
      if (normalizedLabel.split(/\s+/).some(word => word.startsWith(normalizedTerm))) return { option, score: 1 };
      if (normalizedLabel.includes(normalizedTerm)) return { option, score: 2 };
      return null;
    })
    .filter(Boolean)
    .sort((a, b) => a.score - b.score)
    .map(item => item.option);
};

function getSelectedOption(options, value) {
  if (!options) return null;
  const target = value ?? null;
  return options.find(option => !option.divider && (option.value ?? null) === target) || null;
}

export default function SearchableSelect({
  value,
  onChange,
  options,
  disabled,
  readOnly,
  className,
  onFocus,
  onBlur,
  tabIndex,
  searchPlaceholder = 'Search...',
  ariaLabel,
  renderOptionPrefix,
  renderTriggerPrefix,
  variant = 'ghost',
  showSelectedLabel = true,
  dropdownHeader,
  renderOptionExtra,
  onSearchTermChange,
}) {
  const optionsWithoutBlank = useMemo(
    () => (options || []).filter(option => option?.divider || option?.value),
    [options],
  );
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const [menuStyle, setMenuStyle] = useState({});
  const listboxId = useId();
  const wrapperRef = useRef(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const searchRef = useRef(null);
  const optionRefs = useRef({});
  const shouldScrollRef = useRef(false);
  const selectedOption = useMemo(() => getSelectedOption(optionsWithoutBlank, value), [optionsWithoutBlank, value]);
  const isDisabled = disabled || readOnly;
  const filteredOptions = useMemo(
    () => defaultFilterOptions(optionsWithoutBlank, searchTerm),
    [optionsWithoutBlank, searchTerm],
  );

  useEffect(() => {
    if (!open) return;
    const selectedIndex = filteredOptions.findIndex(
      option => (option.value ?? null) === (selectedOption?.value ?? null),
    );
    shouldScrollRef.current = true;
    setActiveIndex(prev => {
      if (selectedIndex !== -1) return selectedIndex;
      const maxIndex = filteredOptions.length - 1;
      if (maxIndex < 0) return 0;
      return Math.min(prev, maxIndex);
    });
  }, [open, filteredOptions, selectedOption]);

  useEffect(() => {
    if (!open || !shouldScrollRef.current) return;
    const activeOption = filteredOptions[activeIndex];
    if (!activeOption) return;
    const optionKey = activeOption.value ?? 'option';
    optionRefs.current[optionKey]?.scrollIntoView({ block: 'nearest', behavior: 'auto' });
    shouldScrollRef.current = false;
  }, [activeIndex, filteredOptions, open]);

  useEffect(() => {
    if (!open) return;

    const handleClick = event => {
      if (triggerRef.current?.contains(event.target) || menuRef.current?.contains(event.target)) return;
      setOpen(false);
      onBlur?.(event);
    };

    document.addEventListener('click', handleClick);

    const focusRafId = requestAnimationFrame(() => searchRef.current?.focus());

    return () => {
      document.removeEventListener('click', handleClick);
      cancelAnimationFrame(focusRafId);
    };
  }, [open, onBlur]);

  const computePosition = () => {
    const toggleRect = triggerRef.current?.getBoundingClientRect();
    const wrapperRect = wrapperRef.current?.getBoundingClientRect();
    if (!toggleRect || !wrapperRect) return null;

    const gap = 4;
    const viewportPadding = 24;
    const preferredHeight = 250;

    let left = toggleRect.left - wrapperRect.left;

    const estimatedWidth = Math.max(250, toggleRect.width);

    if (left + estimatedWidth > wrapperRect.width) {
      left = Math.max(0, wrapperRect.width - estimatedWidth);
    }

    const availableBelow = window.innerHeight - toggleRect.bottom - viewportPadding;
    const availableAbove = toggleRect.top - viewportPadding;
    const shouldOpenUpwards = availableBelow < 180 && availableAbove > availableBelow;
    const availableSpace = Math.max((shouldOpenUpwards ? availableAbove : availableBelow) - gap, 0);
    const maxHeight = Math.min(preferredHeight, availableSpace);

    if (shouldOpenUpwards) {
      return {
        left,
        bottom: wrapperRect.height - (toggleRect.top - wrapperRect.top) + gap,
        maxHeight,
      };
    }

    return {
      top: toggleRect.bottom - wrapperRect.top + gap,
      left,
      maxHeight,
    };
  };

  useEffect(() => {
    if (!open) return;

    const updatePosition = () => {
      const pos = computePosition();
      if (pos) setMenuStyle(pos);
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);

    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [open]);

  const toggleMenu = () => {
    if (isDisabled) return;
    setOpen(prev => {
      const next = !prev;
      if (next) {
        const pos = computePosition();
        if (pos) setMenuStyle(pos);
        setSearchTerm('');
        const nextOptions = defaultFilterOptions(optionsWithoutBlank, '');
        const selectedIndex = nextOptions.findIndex(
          option => (option.value ?? null) === (selectedOption?.value ?? null),
        );
        shouldScrollRef.current = true;
        setActiveIndex(prevIndex => {
          if (selectedIndex !== -1) return selectedIndex;
          const maxIndex = nextOptions.length - 1;
          if (maxIndex < 0) return 0;
          return Math.min(prevIndex, maxIndex);
        });
      }
      return next;
    });
  };

  const handleSelect = (event, optionValue) => {
    event?.preventDefault();
    event?.stopPropagation();
    if (isDisabled) return;
    setOpen(false);
    onChange(optionValue);
  };

  const handleMouseDown = event => {
    if (isDisabled) return;

    event.preventDefault();
    event.stopPropagation();
  };

  const handleClick = event => {
    if (isDisabled) return;

    event.stopPropagation();
    toggleMenu();
  };

  const activeOption = filteredOptions[activeIndex];
  const optionIdPrefix = `${listboxId}-searchable-option`;
  const activeOptionId = activeOption ? `${optionIdPrefix}-${activeOption.value ?? 'option'}` : undefined;
  const handleSearchKeyDown = event => {
    if (!open) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      setOpen(false);
      triggerRef.current?.focus();
      return;
    }
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex(prevIndex => {
        if (!filteredOptions.length) return 0;
        const maxIndex = filteredOptions.length - 1;
        const next = event.key === 'ArrowDown' ? prevIndex + 1 : prevIndex - 1;
        if (next > maxIndex) return 0;
        if (next < 0) return maxIndex;
        return next;
      });
      shouldScrollRef.current = true;
      return;
    }

    if (event.key === 'Enter') {
      const option = filteredOptions[activeIndex];
      if (option) {
        event.preventDefault();
        handleSelect(event, option.value);
      }
    }
  };

  const handleSearchChange = event => {
    const next = onSearchTermChange ? onSearchTermChange(event.target.value) : event.target.value;
    setSearchTerm(next);
  };

  const handleTriggerKeyDown = event => {
    if (event.key === 'Enter' || event.key === ' ' || event.key === 'ArrowDown') {
      event.preventDefault();
      if (!open) {
        toggleMenu();
      }
    }
  };

  const toggleClass =
    variant === 'field'
      ? `relative flex h-10 w-full items-center gap-2 rounded-base border bg-input px-3 pr-4 text-left text-base leading-[2.25rem] text-foreground placeholder:text-foreground-softest transition ${
          open ? 'border-primary' : 'border-base hover:border-primary focus:border-primary focus:outline-none'
        } ${isDisabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`
      : `${isDisabled ? 'bg-transparent' : 'bg-input'} text-foreground border-input outline-hidden relative flex h-9 items-center gap-2 pr-4 text-left leading-[1.375rem] sm:text-sm`;

  return (
    <div className={`relative flex flex-col gap-y-2 ${className || ''}`} ref={wrapperRef}>
      <div>
        <div data-part='root'>
          <button
            type='button'
            aria-label={ariaLabel || selectedOption?.label || 'Select option'}
            data-part='toggle'
            className={toggleClass}
            tabIndex={typeof tabIndex === 'number' ? tabIndex : 0}
            aria-expanded={open}
            aria-haspopup='listbox'
            onMouseDown={handleMouseDown}
            onClick={handleClick}
            onKeyDown={handleTriggerKeyDown}
            onFocus={onFocus}
            onBlur={onBlur}
            disabled={isDisabled}
            ref={el => {
              triggerRef.current = el;
            }}
          >
            <div className='text-foreground [&>span:empty]:text-foreground-softest flex min-h-[1.375rem] w-full items-center truncate [&>span:empty:before]:content-[attr(data-placeholder)]'>
              <span className='flex items-center gap-2 truncate'>
                {renderTriggerPrefix ? (
                  <span
                    className='inline-flex h-5 w-5 shrink-0 items-center justify-center text-[20px] leading-none'
                    aria-hidden
                  >
                    {renderTriggerPrefix(selectedOption?.value)}
                  </span>
                ) : null}
                {showSelectedLabel ? <span className='truncate'>{selectedOption?.label || ''}</span> : null}
              </span>
            </div>

            <svg
              xmlns='http://www.w3.org/2000/svg'
              fill='none'
              className='text-foreground-softest size-5 shrink-0'
              viewBox='0 0 24 24'
              aria-hidden
            >
              <path
                fill='currentColor'
                fillRule='evenodd'
                d='M5.293 8.293a1 1 0 0 1 1.414 0L12 13.586l5.293-5.293a1 1 0 1 1 1.414 1.414l-6 6a1 1 0 0 1-1.414 0l-6-6a1 1 0 0 1 0-1.414'
                clipRule='evenodd'
              />
            </svg>
          </button>
        </div>
      </div>

      <select
        className='hidden'
        aria-hidden
        data-part='select'
        value={selectedOption?.value ?? ''}
        readOnly
        tabIndex={-1}
      >
        {options
          ?.filter(option => !option.divider && option?.value)
          .map(option => (
            <option key={option.value ?? 'option'} value={option.value ?? 'option'}>
              {option.label}
            </option>
          ))}
      </select>

      <div
        role='listbox'
        data-part='listbox'
        className={`rounded-base bg-overlay border-base shadow-base absolute z-50 flex w-full min-w-[248px] flex-col overflow-hidden border transition duration-150 ease-in-out ${
          open ? 'scale-100 opacity-100' : 'scale-95 opacity-0'
        }`}
        hidden={!open}
        ref={menuRef}
        style={menuStyle}
        aria-expanded={open}
      >
        <div className='border-base sticky top-0 flex w-full items-center gap-2 border-b px-3 py-1'>
          <div className='flex min-w-0 flex-1 items-center gap-2'>
            <div className='text-foreground-softest flex size-4 items-center' aria-hidden>
              <svg
                xmlns='http://www.w3.org/2000/svg'
                fill='none'
                viewBox='0 0 24 24'
                className='text-foreground-softest size-4'
              >
                <path
                  fill='currentColor'
                  fillRule='evenodd'
                  d='M10 2a8 8 0 1 0 4.906 14.32l5.387 5.387a1 1 0 0 0 1.414-1.414l-5.387-5.387A8 8 0 0 0 10 2m-6 8a6 6 0 1 1 12 0 6 6 0 0 1-12 0'
                  clipRule='evenodd'
                />
              </svg>
            </div>
            <input
              ref={searchRef}
              className='text-foreground placeholder:text-foreground-softest focus:outline-hidden w-full border-none bg-transparent px-2 py-1.5 shadow-none focus:ring-0 md:text-sm'
              type='text'
              data-part='search-input'
              role='searchbox'
              placeholder={searchPlaceholder}
              value={searchTerm}
              onChange={handleSearchChange}
              onKeyDown={handleSearchKeyDown}
              aria-activedescendant={activeOptionId}
              aria-autocomplete='list'
            />
          </div>
          {dropdownHeader ? <div className='flex items-center'>{dropdownHeader}</div> : null}
        </div>

        <div className='flex grow flex-col overflow-y-auto p-1.5' data-part='options-list'>
          {filteredOptions.map((option, index) => {
            const optionKey = option.value ?? 'option';
            const optionId = `${optionIdPrefix}-${optionKey}`;
            const isSelected = optionKey === (selectedOption?.value ?? 'option');
            const isActive = index === activeIndex;

            return (
              <button
                type='button'
                className='text-left'
                data-part='option'
                data-value={optionKey}
                data-label={option.label}
                role='option'
                aria-selected={isSelected}
                aria-disabled={false}
                key={optionKey}
                id={optionId}
                data-highlighted={isActive ? '' : undefined}
                data-selected={isSelected ? '' : undefined}
                ref={el => {
                  if (el) optionRefs.current[optionKey] = el;
                  else delete optionRefs.current[optionKey];
                }}
                onClick={event => handleSelect(event, option.value)}
                onMouseEnter={() => {
                  setActiveIndex(index);
                  shouldScrollRef.current = true;
                }}
                onMouseMove={() => {
                  if (activeIndex !== index) {
                    setActiveIndex(index);
                    shouldScrollRef.current = true;
                  }
                }}
                tabIndex={-1}
              >
                <div className='rounded-base text-foreground in-data-highlighted:bg-accent flex cursor-default items-center justify-between gap-3 p-2 text-sm'>
                  <span className='flex min-w-0 items-center gap-2'>
                    {renderOptionPrefix ? (
                      <span
                        className='inline-flex h-5 w-5 shrink-0 items-center justify-center text-[20px] leading-none'
                        aria-hidden
                      >
                        {renderOptionPrefix(option.value)}
                      </span>
                    ) : null}
                    <span className='whitespace-normal break-words leading-snug'>{option.label}</span>
                  </span>
                  <span className='text-foreground-soft flex shrink-0 items-center gap-2 text-xs'>
                    {renderOptionExtra ? renderOptionExtra(option.value) : null}
                    <svg
                      className={`${isSelected ? 'block' : 'hidden'} text-foreground size-3.5 shrink-0`}
                      fill='none'
                      viewBox='0 0 24 24'
                      aria-hidden
                    >
                      <path
                        stroke='currentColor'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        strokeWidth='2'
                        d='M20 6 9 17l-5-5'
                      />
                    </svg>
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
